import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Loader2, Sunrise, Droplets, ArrowUp, ArrowDown, Home, Umbrella, Sun, CloudRain, RefreshCw } from "lucide-react";
import { WeatherIcon } from "./WeatherIcon";
import { temp, tempUnit, conditionTheme } from "../lib/weather";

const advice = (day, units) => {
  if (day.pop >= 60) return { Icon: Umbrella, text: "Bawa payung, peluang hujan tinggi" };
  if (day.pop >= 30) return { Icon: CloudRain, text: "Siapkan jas hujan untuk berjaga" };
  if (day.condition.main === "Clear" && temp(day.temp_max, "metric") >= 30) return { Icon: Sun, text: "Cerah & panas, pakai tabir surya" };
  return { Icon: Sun, text: "Cuaca mendukung aktivitas luar ruang" };
};

export const DailySummaryDialog = ({ summary, units }) => {
  const { open, setOpen, settings, toggleEnabled, setTime, cards, loading, showNow, cityCount } = summary;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-3xl glass border-border/60 max-h-[85vh] overflow-y-auto no-scrollbar"
        data-testid="daily-summary-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-400" />
            Ringkasan Pagi
          </DialogTitle>
          <DialogDescription>
            Ringkasan cuaca harian untuk kota rumah &amp; kota favorit, dikirim otomatis setiap pagi saat aplikasi terbuka.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border/60 bg-[hsl(var(--surface)/0.6)] p-4" data-testid="daily-summary-settings">
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={settings.enabled} onCheckedChange={toggleEnabled} data-testid="daily-summary-switch" />
            <span className="text-sm font-semibold">{settings.enabled ? "Aktif" : "Nonaktif"}</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Setiap jam</span>
            <input
              type="time"
              value={settings.time}
              onChange={(e) => e.target.value && setTime(e.target.value)}
              className="glass rounded-xl px-3 py-1.5 num text-sm bg-transparent"
              data-testid="daily-summary-time"
            />
          </label>
          <span className="text-xs text-muted-foreground ml-auto num" data-testid="daily-summary-status">
            {settings.lastSent ? `Terakhir dikirim ${settings.lastSent}` : "Belum pernah dikirim"} · {cityCount} kota
          </span>
          <button
            onClick={showNow}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary text-primary-foreground"
            data-testid="daily-summary-show-now"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Tampilkan sekarang
          </button>
        </div>

        {loading && (
          <div className="grid place-items-center py-16 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && cards.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="daily-summary-empty">
            Klik "Tampilkan sekarang" untuk melihat ringkasan hari ini. Tetapkan kota rumah (ikon rumah) atau favorit agar lebih lengkap.
          </p>
        )}

        {!loading && cards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {cards.map((c) => {
              const th = conditionTheme(c.day.condition.main, c.day.condition.icon);
              const { Icon, text } = advice(c.day, units);
              return (
                <div
                  key={`${c.loc.lat}-${c.loc.lon}`}
                  data-testid={`summary-card-${c.loc.name}`}
                  className="rounded-2xl p-4 border border-border/60 bg-[hsl(var(--surface)/0.6)]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {c.isHome && <Home className="w-4 h-4 text-primary" />}
                      <div>
                        <div className="font-bold">{c.loc.name}</div>
                        <div className="text-xs text-muted-foreground capitalize">{c.day.condition.description}</div>
                      </div>
                    </div>
                    <WeatherIcon icon={c.day.condition.icon} className="w-10 h-10" style={{ color: th.accent }} />
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-sm num">
                    <span className="flex items-center gap-1 text-rose-500 font-semibold">
                      <ArrowUp className="w-4 h-4" />
                      {temp(c.day.temp_max, units)}{tempUnit(units)}
                    </span>
                    <span className="flex items-center gap-1 text-sky-500 font-semibold">
                      <ArrowDown className="w-4 h-4" />
                      {temp(c.day.temp_min, units)}{tempUnit(units)}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Droplets className="w-4 h-4" />
                      {c.day.pop}%
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon className="w-3.5 h-3.5 text-primary" />
                    {text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
